import React from "react";
import { Box, Grid, Skeleton, Typography } from "@mui/material";
import { AnimatePresence, motion } from "framer-motion";
import { fadeUp, revealProps, stagger, useReducedMotion } from "homepage-new/motion";
import { rf } from "../theme";
import { Band, Card, Display, Pill, PushButton } from "../ui";
import { fillOf, statusOf } from "../cohortStatus";

const PHASE_FILLS = [rf.coral, rf.amber, rf.mint, rf.sky, rf.lilac];

const Phase = ({ phase, index, last }) => (
  <Box sx={{ display: "flex", gap: { xs: 2, md: 2.5 } }}>
    <Box sx={{ display: "flex", flexDirection: "column", alignItems: "center", flexShrink: 0 }}>
      <Box
        sx={{
          width: 44,
          height: 44,
          borderRadius: rf.radiusSm,
          display: "grid",
          placeItems: "center",
          backgroundColor: PHASE_FILLS[index % PHASE_FILLS.length],
          border: rf.line,
          boxShadow: rf.shadowSm,
          fontFamily: rf.display,
          fontWeight: 800,
          fontSize: 17,
          color: rf.ink,
        }}
      >
        {index + 1}
      </Box>
      {!last && <Box sx={{ width: 3, flex: 1, minHeight: 28, backgroundColor: rf.ink, mt: 1.25, borderRadius: 2 }} />}
    </Box>

    <Box sx={{ pb: last ? 0 : 4, flex: 1 }}>
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.5, flexWrap: "wrap", mb: 1.25 }}>
        <Display component="h4" size={22}>
          {phase.title}
        </Display>
        <Pill bg={rf.creamDeep} sx={{ fontSize: 11.5 }}>
          {phase.weeks}
        </Pill>
      </Box>

      <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, display: "flex", flexDirection: "column", gap: 0.9, mb: 1.75 }}>
        {phase.modules.map((m) => (
          <Box component="li" key={m} sx={{ display: "flex", gap: 1.25, alignItems: "flex-start" }}>
            <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: rf.ink, mt: "7px", flexShrink: 0 }} />
            <Typography sx={{ fontFamily: rf.body, fontSize: 14.5, color: rf.ink, lineHeight: 1.55 }}>{m}</Typography>
          </Box>
        ))}
      </Box>

      {phase.outcome && (
        <Box sx={{ px: 2, py: 1.25, backgroundColor: rf.cream, border: rf.lineThin, borderRadius: rf.radiusSm }}>
          <Typography sx={{ fontFamily: rf.body, fontSize: 14, fontWeight: 600, color: rf.ink }}>
            → {phase.outcome}
          </Typography>
        </Box>
      )}
    </Box>
  </Box>
);

const Points = ({ title, items, dot }) => (
  <>
    <Typography sx={{ fontFamily: rf.display, fontSize: 11.5, fontWeight: 800, letterSpacing: "0.08em", textTransform: "uppercase", color: rf.inkFaint, mb: 1 }}>
      {title}
    </Typography>
    <Box component="ul" sx={{ listStyle: "none", p: 0, m: 0, mb: 3, display: "flex", flexDirection: "column", gap: 0.85 }}>
      {items.map((item) => (
        <Box component="li" key={item} sx={{ display: "flex", gap: 1.25 }}>
          <Box sx={{ width: 8, height: 8, borderRadius: "2px", backgroundColor: dot, border: rf.lineThin, mt: "6px", flexShrink: 0 }} />
          <Typography sx={{ fontFamily: rf.body, fontSize: 14, color: rf.ink, lineHeight: 1.5 }}>{item}</Typography>
        </Box>
      ))}
    </Box>
  </>
);

export const Syllabus = ({ cohorts = [], selectedSlug, onSelect, loading }) => {
  const reduced = useReducedMotion();
  const cohort = cohorts.find((c) => c.slug === selectedSlug) || cohorts[0];
  const phases = cohort?.syllabus || [];

  return (
    <Band
      id="curriculum"
      eyebrow="The whole syllabus, up front"
      title="Every week, published before you pay"
      lead="A cohort cannot be tailored to you — so the least we can do is show you exactly what it covers, in order, before you decide. Nothing here is revealed later."
    >
      {loading && (
        <Box sx={{ maxWidth: 860, mx: "auto" }}>
          <Skeleton variant="rounded" height={48} width={340} sx={{ mx: "auto", mb: 5 }} />
          {[0, 1, 2].map((i) => (
            <Skeleton key={i} variant="rounded" height={130} sx={{ mb: 3 }} />
          ))}
        </Box>
      )}

      {!loading && cohort && (
        <>
          {cohorts.length > 1 && (
            <Box sx={{ display: "flex", flexWrap: "wrap", justifyContent: "center", gap: 1.5, mb: { xs: 4, md: 5 } }}>
              {cohorts.map((c) => (
                <PushButton
                  key={c.slug}
                  variant={c.slug === cohort.slug ? "dark" : "light"}
                  onClick={() => onSelect(c.slug)}
                >
                  {c.title}
                </PushButton>
              ))}
            </Box>
          )}

          <AnimatePresence mode="wait">
            <motion.div
              key={cohort.slug}
              initial={{ opacity: 0, y: 12 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -12 }}
              transition={{ duration: 0.3, ease: [0.22, 0.61, 0.36, 1] }}
            >
              <Card shadow={rf.shadowLg} sx={{ p: { xs: 2.5, sm: 4, md: 5 }, textAlign: "left" }}>
                <Grid container spacing={{ xs: 3, md: 5 }}>
                  <Grid item xs={12} md={4}>
                    <Box sx={{ position: { md: "sticky" }, top: { md: 110 } }}>
                      <Pill bg={fillOf(cohort)} sx={{ mb: 1.75, textTransform: "uppercase", fontSize: 11 }}>
                        {statusOf(cohort).label}
                      </Pill>
                      <Display component="h3" size={28} sx={{ mb: 1.5 }}>
                        {cohort.title}
                      </Display>
                      <Typography sx={{ fontFamily: rf.body, fontSize: 15, color: rf.inkSoft, lineHeight: 1.6, mb: 3 }}>
                        {cohort.description}
                      </Typography>

                      <Points title="You should already" items={cohort.prerequisites || []} dot={rf.creamDeep} />
                      <Points title="You leave able to" items={cohort.outcomes || []} dot={rf.mint} />
                    </Box>
                  </Grid>

                  <Grid item xs={12} md={8}>
                    <motion.div variants={stagger(0.06)} {...revealProps(reduced)}>
                      {phases.map((phase, i) => (
                        <motion.div key={phase.id} variants={fadeUp}>
                          <Phase phase={phase} index={i} last={i === phases.length - 1} />
                        </motion.div>
                      ))}
                    </motion.div>
                  </Grid>
                </Grid>
              </Card>
            </motion.div>
          </AnimatePresence>
        </>
      )}
    </Band>
  );
};

export default Syllabus;
